import { HTTPStatuses, type HttpError } from '@packages/domain/constants/http';
import { isHttpErrorWithStatus } from '@packages/domain/utils/errors/errors';
import { fetchUser } from 'actions/entities';
import { getRefreshToken, isFetching } from 'selectors';
import type { PayexService } from 'services/payex';
import type {
  PayexDispatch,
  PayexGetState,
  PayexThunkAction,
} from 'store/configureStore';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';
import {
  IMPERSONATE_FAKE_REFRESH_TOKEN,
  INTERNAL_SERVER_FAILURE,
  LOGIN_USER_FAILURE,
  LOGIN_USER_REQUEST,
  LOGIN_USER_SUCCESS,
  REFRESH_TOKEN,
} from '../../constants';

const USER_NEEDS_TO_CHANGE_PASSWORD = 'user_needs_to_change_password';

type LoginResponse = {
  accessToken: string;
  expiresIn: number;
  refreshToken?: string;
  tokenType: string;
  updatePassword?: boolean;
};

const logInUser = async (
  user: LogInUserProps,
  dispatch: PayexDispatch,
  getState: PayexGetState,
  payex: PayexService,
  actions: Record<string, string>,
  dispatchPayload = false,
) => {
  const GRANT_TYPE = 'password';
  const state = getState();
  const isFetchingAuthentication = isFetching(state, 'authentication');
  const { requestAction, successAction, failureAction } = actions;

  if (isFetchingAuthentication) {
    return Promise.resolve();
  }

  dispatch({ type: requestAction });

  const { username, password, captchaResponse } = user;

  try {
    const response = await payex.auth.logIn({
      username,
      password,
      grant_type: GRANT_TYPE,
      ...(captchaResponse && { captcha_response: captchaResponse }),
    });

    const { updatePassword = false } = response;

    if (updatePassword) {
      throw userNeedsToChangePasswordError();
    }

    if (!dispatchPayload) {
      return dispatch({ type: successAction });
    }

    const payload = normalizeAuthPayload(response);

    dispatch({
      type: successAction,
      payload: { authentication: payload, user: { email: username } },
    });

    return dispatch(fetchUser());
  } catch (error) {
    if (isHttpErrorWithStatus(error)) {
      const httpError = error as HttpError;
      if (httpError.status === HTTPStatuses.INTERNAL_SERVER_ERROR) {
        dispatch({ type: INTERNAL_SERVER_FAILURE });
      }
    }

    dispatch({ type: failureAction });
    throw error;
  }
};

const userNeedsToChangePasswordError = () => {
  const error: HttpError = new Error(USER_NEEDS_TO_CHANGE_PASSWORD);
  error.parsedBody = { errors: [{ error: USER_NEEDS_TO_CHANGE_PASSWORD }] };

  return error;
};

type LogInUserProps = {
  username: string;
  password: string;
  captchaResponse?: string;
};

export const logIn =
  (user: LogInUserProps): PayexThunkAction =>
  async (dispatch, getState, { payex }) => {
    const actions = {
      requestAction: LOGIN_USER_REQUEST,
      successAction: LOGIN_USER_SUCCESS,
      failureAction: LOGIN_USER_FAILURE,
    };

    const dispatchPayload = true;

    return logInUser(user, dispatch, getState, payex, actions, dispatchPayload);
  };

export const refreshToken =
  (): PayexThunkAction =>
  async (dispatch, getState, { payex }) => {
    const GRANT_TYPE = 'refresh_token';
    const state = getState();
    const refreshToken = getRefreshToken(state);

    if (refreshToken === IMPERSONATE_FAKE_REFRESH_TOKEN) {
      return;
    }

    try {
      const response = await payex.auth.refreshToken({
        refresh_token: refreshToken as string,
        grant_type: GRANT_TYPE,
      });

      const payload = normalizeAuthPayload(response);
      return dispatch(setRefreshToken(payload));
    } catch (error) {
      if (isHttpErrorWithStatus(error)) {
        const { status } = error as HttpError;

        if (status !== HTTPStatuses.UNAUTHORIZED) {
          errorNotifier.notify(error);
        }
      }
    }
  };

export const setRefreshToken = (payload = {}) => ({
  type: REFRESH_TOKEN,
  payload,
});

export const normalizeAuthPayload = (payload: LoginResponse) => {
  const expiresIn = payload.expiresIn;
  const currentTimestamp = Math.round(Date.now() / 1000);
  const expiresInTimestamp = currentTimestamp + expiresIn;

  return { ...payload, expiresIn: expiresInTimestamp };
};
